/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFile, DownloadJobTasksElem} from '@ion/core/src/types/entities';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {getAppId} from '@ion/dls/src/editors/DeviceFileEditorUtils';
import {useDeviceFiles} from '@ion/query/src/hooks/useDeviceFiles';
import {
  CONFIG,
  DEFAULT_DEVICE_TYPES,
  onRenderFileNameOption,
  onRenderInput
} from './helpers';
import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';

// defines the file name chooser prop definitions ...
export type JobTaskFileNameChooserProps = {
  row: DownloadJobTasksElem;
  accountId: number;
  includeSubAccounts: boolean;
  isDevicesSelected?: boolean;
  devicePlatformIds?: Array<Number>;
  onChange: (event: ChangeEvent, value: DeviceFile) => void;
  suppressNullVersions?: boolean;
}

// defines the styles used by the file name chooser ...
export const useStyles = makeStyles(() => ({
  spinner : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  }
}));

/**
 * The JobTaskFileNameChooser provides a list of file names for the current account
 * and allows the user to select a file name from the list.
 *
 * ```typescript
 *
 *     import {JobTaskFileNameChooser} from './JobTaskFileNameChooser';
 *
 *     // with callback subscription ...
 *     <JobTaskFileNameChooser onChange={handleFileName(value, index, tasks)} />
 *
 * ```
 */
export const JobTaskFileNameChooser = ({
  row,
  accountId,
  includeSubAccounts,
  devicePlatformIds,
  onChange,
  isDevicesSelected = false,
  suppressNullVersions = false
}: JobTaskFileNameChooserProps) => {
  const classes             = useStyles();
  const {task}              = row;
  const {dropdownProps}     = useJobTaskChooserStyles();
  const onGetSelectedOption = useCallback((option, value) => option?.name === value?.name, []);
  const onGetOptionLabel    = useCallback(fileName => fileName?.name ? fileName?.name : '', []);
  const usesAppIds          = task?.usesAppIds;
  const appIds              = task?.appId?.length ? [task.appId] : task.deviceAppIds;
  const isVbus              = DEFAULT_DEVICE_TYPES?.includes(task?.deviceType) && task?.fileType !== CONFIG;

  const {deviceFiles, isLoading} = useDeviceFiles({
    enabled           : !!task?.deviceType,
    devicePlatformIds : isVbus ? devicePlatformIds : [],
    platformIds       : [task?.platformId],
    enabledOnly       : true,
    ...(usesAppIds && {appIds}),
    suppressNullVersions,
    includeSubAccounts,
    accountId
  });

  return (
    isLoading ? (
      <div className={classes.spinner}>
        <IonSpinner width='25px' height='25px' />
      </div>
    ) : (
      <Autocomplete disableClearable
        disabled={!isDevicesSelected || task?.deviceType === '' || task?.fileType === undefined || task?.fileType === ''}
        options={deviceFiles || []}
        value={{name: task?.fileLabel}}
        getOptionSelected={onGetSelectedOption}
        filterOptions={filterOptions}
        classes={dropdownProps}
        getOptionLabel={onGetOptionLabel}
        renderOption={onRenderFileNameOption}
        onChange={onChange}
        renderInput={onRenderInput(translate('jobcreate:fields.fileName.placeholder'))} />
    )
  );
};

/**
 * Filters options by comparing the current dropdown value with each option. If an option
 * contains the value in its name, file version, or app ID, it is included in the returned
 * list.
 */
const filterOptions = (options: DeviceFile[], {inputValue}: {inputValue: string}) => {
  if (inputValue === '') {
    return options;
  } else {
    const files = [];
    const value = inputValue.toLowerCase();
    options.forEach(option => {
      const name        = option?.name?.toLowerCase();
      const {fields}    = option?.validationMetaData;
      const fileVersion = (fields?.version)?.toString()?.toLowerCase();
      const appId       = getAppId(option)?.toLowerCase() || '';
      if (name?.includes(value) ||
        fileVersion?.includes(value) ||
        appId?.includes(value)) {
        files.push(option);
      }
    });
    return files;
  }
};

export default JobTaskFileNameChooser;
